import { Component, Input, OnInit, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { DialogService } from 'rev-shared/ui/dialog/Dialog.Service';
import { IWebcastViewContext } from 'rev-shared/webcast/webcastView/IWebcastViewContext';
import { noop } from 'rev-shared/util';
import { IPollAnswer } from 'rev-portal/scheduledEvents/polls/Polls.Contract';
import { Poll } from 'rev-portal/scheduledEvents/polls/Poll';
import { Polls, POLL_ANSWERS_DEFAULT } from 'rev-portal/scheduledEvents/polls/Polls';
import { WebcastModel } from 'rev-portal/scheduledEvents/webcast/model/WebcastModel';

enum POLLS_VIEW {
	List = 'list',
	Edit = 'edit'
}

type TPollData = Poll & { active: boolean };

import styles from './VbWebcastPollsView.Component.module.less';

@Component({
	selector: 'vb-webcast-polls-view',
	templateUrl: './VbWebcastPollsView.Component.html',
	host: {
		'[class]': 'styles.root'
	},
	standalone: false,
	styleUrl: '../WebcastSidebar.ng.less'
})
export class VbWebcastPollsViewCompoment implements OnInit {
	@Input() public polls: Polls;
	@Input() public $polls: any[]; //workaround to get notifyed when polls collection changed. will work on observable later.
	@Input() public context: IWebcastViewContext;

	public pollToEdit: TPollData;
	public status: { [key: string]: boolean };
	public view = POLLS_VIEW.List;
	public webcast: WebcastModel;

	public closedPolls: any[] = [];
	public openPolls: any[] = [];
	public publishedPolls: any[] = [];

	public readonly POLLS_VIEW = POLLS_VIEW;
	public readonly styles = styles;

	constructor(
		private readonly Dialog: DialogService,
		private readonly TranslateService: TranslateService
	) {}

	public ngOnInit(): void {
		this.webcast = this.polls.webcast;
		this.status = { 'loaded': true };
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.$polls && this.$polls) {
			this.onChangePollsCollection();
		}

	}

	public pollTrackBy(index: number, item: Poll): string {
		return item.id;
	}

	private onChangePollsCollection(): void {
		this.polls.onPollsCollectionChanged();
		this.openPolls = (this.polls.$polls || []).filter(p => p.isOpen);
		this.closedPolls = (this.polls.$polls || []).filter(p => !p.isOpen);
		this.publishedPolls = (this.polls.$polls || []).filter(p => p.isPublished);
	}

	public setEditView(poll: Poll): void {
		this.pollToEdit = this.getPollToEdit(poll);
		this.view = POLLS_VIEW.Edit;
	}

	public setCreateView(): void {
		this.pollToEdit = this.getPollToEdit();
		this.view = POLLS_VIEW.Edit;
	}

	public deletePoll(poll: Poll): void {
		const params = {
			message: this.TranslateService.instant('Event_DeletePoll_Message'),
			title: this.TranslateService.instant('Event_DeletePoll'),
			actionText: this.TranslateService.instant('Delete'),
			cancelText: this.TranslateService.instant('Cancel'),
			themed: true
		};

		this.Dialog.openConfirmationDialog(params).result
			.then(() => this.polls.delete(poll))
			.catch(noop);
	}

	public savePollForm(poll: Poll): void {
		this.pollToEdit = null;
		this.stripEmptyAnswers(poll);
		poll.active = true;

		if (poll.isNew) {
			poll.isNew = false;
			poll.active = true;
			this.polls.add(poll);
		} else {
			const pollToUpdate = (this.polls.$polls || []).find(p => p.id === poll.id);
			Object.assign(pollToUpdate, poll);
			this.polls.update(pollToUpdate);
		}
		this.setListView();
	}

	private setListView(): void {
		this.view = POLLS_VIEW.List;
	}

	private getPollToEdit(poll?: Poll): TPollData {
		const answersRangeCount = poll?.answers?.length ? Math.max(poll.answers.length, POLL_ANSWERS_DEFAULT) : POLL_ANSWERS_DEFAULT;
		const answersRange = Array.from({ length: answersRangeCount }, (_, index) => index);

		return Object.assign({
			isNew: !poll,
			active: false,
			multipleChoice: poll?.multipleChoice ?? false
		}, poll, {
			totalResponses: 0,
			answers: answersRange.map(i => {
				const { text } = (poll && poll.answers && poll.answers[i]) || {} as IPollAnswer;
				return { text, count: 0 };
			})
		});
	}

	private stripEmptyAnswers(poll: Poll): void {
		poll.answers = (poll.answers || []).filter(answer => answer.text);
	}
}
